import type { DiscardDialogContextValue } from './context'
import type { Action as HistoryAction } from 'history'
import type { BlockerFunction, Location } from 'react-router-dom'

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { useBlocker, useNavigate } from 'react-router-dom'

import { Button, Modal } from '../../components/shared/NarusMaterial'

import { DiscardDialogContext } from './context'

interface Props {
  children: React.ReactNode
}

const enum ActionType {
  BlockTransition = 'block-transition',
  Show = 'show',
  Reset = 'reset',
  Cancel = 'cancel',
  Discard = 'discard',
  FormReset = 'form-reset',
  FormChange = 'form-change'
}

type Action =
  | { type: ActionType.BlockTransition, payload: NonNullable<State['transition']> }
  | { type: ActionType.Show, payload: { form: string, discardCallback?: () => void } }
  | { type: ActionType.Reset }
  | { type: ActionType.Cancel }
  | { type: ActionType.Discard }
  | { type: ActionType.FormReset, payload: { form: string } }
  | { type: ActionType.FormChange, payload: { form: string } }

interface Form {
  isDirty: boolean
}

interface State {
  forms: Record<string, Form>
  isShownForForm?: string
  discardCallback?: () => void

  transition?: {
    currentLocation: Location
    nextLocation: Location
    historyAction: HistoryAction
  }
}

let initialState: State = {
  forms: {}
}

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case ActionType.Show:
      return {
        ...state,
        isShownForForm: action.payload.form,
        discardCallback: action.payload.discardCallback
      }

    case ActionType.BlockTransition:
      return {
        ...state,
        isShownForForm: Object.keys(state.forms).find(key => state.forms[key]?.isDirty === true),
        transition: action.payload
      }

    case ActionType.FormChange:
      return {
        ...state,
        forms: {
          ...state.forms,
          [action.payload.form]: {
            ...state.forms[action.payload.form],
            isDirty: true
          }
        }
      }

    case ActionType.Cancel:
      return {
        ...state,
        isShownForForm: undefined
      }

    case ActionType.Discard:
      return {
        ...state,
        isShownForForm: undefined,
        discardCallback: undefined,
        transition: undefined,
        forms: typeof state.isShownForForm === 'string'
          ? {
            ...state.forms,
            [state.isShownForForm]: {
              ...state.forms[state.isShownForForm],
              isDirty: false
            }
          }
          : state.forms
      }

    case ActionType.Reset:
      return initialState

    case ActionType.FormReset:
      return {
        ...state,
        forms: {
          ...state.forms,
          [action.payload.form]: {
            ...state.forms[action.payload.form],
            isDirty: false
          }
        }
      }

    default:
      return state
  }
}

export function GlobalDiscardDialog ({ children }: Props): JSX.Element {
  let [ state, dispatch ] = useReducer(reducer, initialState)
  let { forms, discardCallback, transition } = state

  let navigate = useNavigate()
  let transitionRef = useRef(transition)

  let navigationBlocker = useCallback<BlockerFunction>(({ currentLocation, nextLocation, historyAction }) => {
    let isSamePathname = currentLocation.pathname === nextLocation.pathname

    if (isSamePathname) {
      return false
    }

    let hasDirtyForms = !!Object
      .keys(forms)
      .find(key => forms[key]?.isDirty === true)

    if (!hasDirtyForms) {
      return false
    }

    dispatch({
      type: ActionType.BlockTransition,
      payload: {
        currentLocation,
        nextLocation,
        historyAction
      }
    })

    return true
  }, [ forms ])

  useBlocker(navigationBlocker)

  useEffect(() => {
    if (transitionRef.current && !transition) {
      let { nextLocation, historyAction } = transitionRef.current

      navigate(nextLocation, {
        replace: historyAction === 'REPLACE'
      })
    }

    transitionRef.current = transition
  }, [ transition, navigate ])

  let handleCancel = useCallback(() => {
    dispatch({
      type: ActionType.Cancel
    })
  }, [])

  let handleDiscard = useCallback(() => {
    discardCallback?.()

    dispatch({
      type: ActionType.Discard
    })
  }, [ discardCallback ])

  let isFormDirty = useCallback<DiscardDialogContextValue['isFormDirty']>(form => {
    return forms[form]?.isDirty === true
  }, [ forms ])

  let show = useCallback<DiscardDialogContextValue['show']>((form, discardCallback) => {
    if (typeof form === 'undefined') {
      form = Object
        .keys(forms)
        .find(key => forms[key]?.isDirty === true)
    }

    let isDirty = typeof form === 'undefined'
      ? false
      : forms[form]?.isDirty === true

    if (isDirty) {
      dispatch({
        type: ActionType.Show,
        payload: {
          form: form as string,
          discardCallback
        }
      })

      return
    }

    discardCallback?.()
  }, [ forms ])

  let reset = useCallback<DiscardDialogContextValue['reset']>(callback => {
    dispatch({
      type: ActionType.Reset
    })

    callback?.()
  }, [])

  let resetForm = useCallback<DiscardDialogContextValue['resetForm']>((form, resetCallback) => {
    if (typeof form !== 'string') {
      console.warn('GlobalDiscardDialog: resetForm() called with invalid form name')
      return
    }

    dispatch({
      type: ActionType.FormReset,
      payload: {
        form
      }
    })

    resetCallback?.()
  }, [])

  let onFormChange = useCallback<DiscardDialogContextValue['onFormChange']>(form => {
    if (typeof form !== 'string') {
      return
    }

    dispatch({
      type: ActionType.FormChange,
      payload: {
        form
      }
    })
  }, [])

  let contextValue = useMemo<DiscardDialogContextValue>(() => ({
    isFormDirty,
    show,
    reset,
    resetForm,
    onFormChange
  }), [ isFormDirty, show, onFormChange, reset, resetForm ])

  return (
    <div className='discard-dialog-wrapper'>
      <DiscardDialogContext.Provider value={contextValue}>
        {children}
      </DiscardDialogContext.Provider>

      {state.isShownForForm && (
        <Modal
          noSticky
          title='Some forms are changed and not saved. Discard changes?'
          actions={[
            <Button
              key='b'
              grey
              label='Cancel'
              // @ts-expect-error // It's okay
              onClick={handleCancel}
            />,
            <Button
              key='a'
              label='Discard'
              // @ts-expect-error // It's okay
              onClick={handleDiscard}
            />
          ]}
          open
        />
      )}
    </div>
  )
}

export * from './context'
export * from './with-discard-dialog'
